import { PropsWithChildren, SyntheticEvent, useCallback } from 'react';
import styled from 'styled-components';
import { SelectProps } from '../../../types/Select/SelectProps';
import { units } from '../../../helpers/styles/units';
import { colors } from '../../../constants/Colors';
import { typography } from '../../../helpers/styles/typography';
import { applyStyleOptionsByType } from './applyStyleOptionsByType';
import { SelectOption } from '../../../types/Select/SelectOption';
import { TypesSelect } from '../../../types/TypesSelect';
import { FontWeight } from '../../../constants/Styles/fontWeight';

export interface OptionsProps {
  options: SelectProps['options'];
  field?: string;
  onClick?: SelectProps['onClick']
  opened: SelectProps['opened'];
  openedDown?: SelectProps['openedDown'];
  selectType: SelectProps['selectType'];
  onChange?: (event?: SyntheticEvent) => void;
  trackName?: boolean
}

const StyledOption = styled.div<Pick<OptionsProps, 'openedDown' | 'selectType'>>`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  align-content: center;
  padding: ${units(4)};

  color: ${colors.grayscale80};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${colors.primaryLight};
  }

  ${typography(5)};
`;

const StyledPrefix = styled.div`
  margin-right: ${units(4)};
`;

const StyledNameWrapper = styled.div`
  font-weight: ${FontWeight.REGULAR};
`;

const StyledPostfix = styled.div`
`;

const StyledOptions = styled.div<Pick<OptionsProps, 'openedDown' | 'opened' | 'selectType'>>`
  display: flex;
  flex-direction: ${({ openedDown }) => (openedDown ? 'column' : 'column-reverse')};
  overflow-y: auto;

  max-height: ${units(20 * 6)}; // 6 - кол-во видимых элементов, 20 - половина высоты одного элемента
  height: auto;
  width: 100%;
  min-width: max-content;

  background: ${colors.light};
  border-bottom: none;
  border-radius: inherit;

  ${applyStyleOptionsByType};

  &::-webkit-scrollbar {
    width: ${units(3)};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.grayscale05};
    border-radius: ${units(2)};
  }
`;

const StyledNameOption = styled.p<Pick<SelectOption, 'selected'>>`
  margin: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  color: ${({ selected }) => (selected ? colors.primaryDark : colors.grayscale80)};

  ${typography(10)};
`;

const StyledLabelOption = styled.p`
  margin: 0;

  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  ${typography(1)};
`;

const Option = ({
    id,
    name,
    field,
    label,
    selected,
    prefix,
    postfix,
    onClick,
    openedDown,
    selectType = TypesSelect.DEFAULT,
    trackName,
}: PropsWithChildren<SelectOption>) => {
    const handleClick = useCallback(() => {
        if (onClick && field) {
            if (trackName) {
                onClick(field, name, false);
                return;
            }

            onClick(field, id, false);
        }
    }, [trackName, onClick, field, id]);

    return (
        <StyledOption
            onClick={handleClick}
            openedDown={openedDown}
            selectType={selectType}
        >
            {prefix && <StyledPrefix>{prefix}</StyledPrefix>}
            <StyledNameWrapper>
                <StyledNameOption selected={selected}>{name}</StyledNameOption>
                <StyledLabelOption>{label}</StyledLabelOption>
            </StyledNameWrapper>
            {postfix && <StyledPostfix>{postfix}</StyledPostfix>}
        </StyledOption>
    );
};

export const Options = ({
    options,
    onClick,
    field,
    openedDown,
    opened,
    selectType = TypesSelect.DEFAULT,
    trackName,
}: PropsWithChildren<OptionsProps>) => (
    <StyledOptions className="options" openedDown={openedDown} selectType={selectType} opened={opened}>
        {options?.map(({ ...option }: SelectOption) => (
            <Option
                selectType={selectType}
                openedDown={openedDown}
                onClick={onClick}
                key={option.id}
                field={field}
                trackName={trackName}
                {...option}
            />
        ))}
    </StyledOptions>
);
